import { fmtBRL } from '@/lib/utils';
import { LancDetailHeader } from './LancDetailHeader';
import { LancDocSection } from './LancDocSection';
import type { Lancamento } from './types';

interface LancDetailModuleProps {
  item: Lancamento;
  onBack: () => void;
}

function StatusBadge({ status }: { status: Lancamento['status'] }) {
  const ok = status === 'ok';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 10px',
        borderRadius: 'var(--r-pill)',
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'var(--font-mono)',
        background: ok ? 'var(--pass-soft)' : 'var(--warn-soft)',
        color: ok ? 'var(--pass)' : 'var(--warn)',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: ok ? 'var(--pass)' : 'var(--warn)',
          flexShrink: 0,
        }}
      />
      {ok ? 'conciliado' : 'pendente'}
    </span>
  );
}

function OcrBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '4px 9px',
        borderRadius: 'var(--r-pill)',
        background: 'var(--lilac-soft, rgba(167,139,250,0.12))',
        color: 'var(--lilac-deep)',
        fontSize: 10,
        fontWeight: 700,
        fontFamily: 'var(--font-mono)',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: 'var(--lilac-deep)',
          flexShrink: 0,
        }}
      />
      OCR · 96% confiança
    </span>
  );
}

/**
 * Tela de detalhe de lançamento.
 * Renderizada por LancamentosModule via estado local — sem rota nova.
 */
export function LancDetailModule({ item, onBack }: LancDetailModuleProps) {
  const fields: [string, string, boolean][] = [
    ['Fornecedor', item.forn, false],
    ['CNPJ', item.cnpj || '—', true],
    ['Valor', fmtBRL(item.val), true],
    ['Emissão', item.emissao, true],
    ['Vencimento', item.venc, true],
    ['Categoria', item.cat, false],
    ['Forma de pagamento', item.pgto, false],
  ];

  return (
    <div
      style={{
        padding: '8px 18px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowY: 'auto',
        minHeight: 0,
      }}
    >
      <LancDetailHeader title={item.forn} onBack={onBack} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <StatusBadge status={item.status} />
        {item.ocr && <OcrBadge />}
      </div>

      {/* Valor destaque */}
      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: 18,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 32,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            color: 'var(--fg)',
          }}
        >
          {fmtBRL(item.val)}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--fg-muted)',
            marginTop: 4,
            fontFamily: 'var(--font-mono)',
          }}
        >
          {item.cat} · {item.date}/2026
        </div>
      </div>

      {/* KV grid */}
      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: '4px 14px',
        }}
      >
        {fields.map(([k, v, mono], i) => (
          <div
            key={k}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '11px 0',
              borderBottom: i < fields.length - 1 ? '1px solid var(--card-stroke)' : 'none',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--fg-muted)', flexShrink: 0 }}>{k}</span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                textAlign: 'right',
                fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
                fontVariantNumeric: mono ? 'tabular-nums' : undefined,
                color: 'var(--fg)',
              }}
            >
              {v}
            </span>
          </div>
        ))}
      </div>

      <LancDocSection item={item} />
    </div>
  );
}
